import accidentService from "services/accidentService";
import declarationOfflineService from "services/declarationOfflineService";
import Errors from "constants/Errors";
import type { OfflineAccident } from "models/Accident";
import type { OfflineSecurite } from "models/Securite";
import securiteService from "./securiteService";
import { dateUtil } from "@sdeapps/react-core";
import type { OfflineDangerGrave } from "models/DangerGrave";
import dangerService from "./dangerService";

/**
 * Tente d'envoyer en BDD toutes les déclarations enregistrées en local
 * @returns Un booléen, true si une des synchronisations a réussi, indiquant un
 * potentiel besoin de rafraîchissement de l'affichage
 */
async function synchronizeOfflineAndOnline(): Promise<void> {
  const fetches: Array<Promise<void>> = [];

  const [offlineAccidents, offlineSecurites, offlineDgis] = await Promise.all([
    declarationOfflineService.getAllAccidents(),
    declarationOfflineService.getAllSecurites(),
    declarationOfflineService.getAllDangerGrave(),
  ]);

  offlineAccidents.forEach((accident) => {
    if (isNotSendingOrSinceMoreThan1MinutesAgo(accident)) {
      fetches.push(syncOfflineAccident(accident));
    }
  });
  offlineSecurites.forEach((securite) => {
    if (isNotSendingOrSinceMoreThan1MinutesAgo(securite)) {
      fetches.push(syncOfflineSecurite(securite));
    }
  });
  offlineDgis.forEach((dangerGrave) => {
    if (isNotSendingOrSinceMoreThan1MinutesAgo(dangerGrave)) {
      fetches.push(syncOfflineDangerGrave(dangerGrave));
    }
  });

  await Promise.all(fetches);
}

async function syncOfflineAccident(accident: OfflineAccident): Promise<void> {
  try {
    await declarationOfflineService.setAccidentSendingById(accident.id, true);
    await declarationOfflineService.setAccidentLastSyncAttempt(accident.id);
    await accidentService.create(accident);
    await declarationOfflineService.removeAccidentById(accident.id);
  } catch (error: unknown) {
    await disableAccidentSending(accident.id);
    if ((error as Error)?.message === Errors.ALREADY_CREATED) {
      await declarationOfflineService.removeAccidentById(accident.id);
    }
    console.error("Error creating accident from offline", error);
  } finally {
    void disableAccidentSending(accident.id);
  }
}

async function syncOfflineSecurite(securite: OfflineSecurite): Promise<void> {
  try {
    await declarationOfflineService.setSecuriteSendingById(securite.id, true);
    await declarationOfflineService.setSecuriteLastSyncAttempt(securite.id);
    await securiteService.create(securite);
    await declarationOfflineService.removeSecuriteById(securite.id);
  } catch (error: unknown) {
    await disableSecuriteSending(securite.id);
    if ((error as Error)?.message === Errors.ALREADY_CREATED) {
      await declarationOfflineService.removeSecuriteById(securite.id);
    }
    console.error("Error creating securite from offline", error);
  } finally {
    void disableSecuriteSending(securite.id);
  }
}

async function syncOfflineDangerGrave(dangerGrave: OfflineDangerGrave): Promise<void> {
  try {
    await declarationOfflineService.setDangerSendingById(dangerGrave.id, true);
    await declarationOfflineService.setDangerLastSyncAttempt(dangerGrave.id);
    await dangerService.create(dangerGrave);
    await declarationOfflineService.removeDangerById(dangerGrave.id);
  } catch (error: unknown) {
    await disableDangerSending(dangerGrave.id);
    if ((error as Error)?.message === Errors.ALREADY_CREATED) {
      await declarationOfflineService.removeDangerById(dangerGrave.id);
    }
    console.error("Error creating dangerGrave from offline", error);
  } finally {
    void disableDangerSending(dangerGrave.id);
  }
}

/**
 * Return vrai lorsque l'élément est en cours d'envoi ou lorsque le dernier essai a plus de 1 minute
 */
function isNotSendingOrSinceMoreThan1MinutesAgo(
  offlineItem: OfflineAccident | OfflineSecurite | OfflineDangerGrave
): boolean {
  return (
    !offlineItem.sending ||
    (offlineItem.sending &&
      dateUtil.differenceInMinutes(undefined, offlineItem.lastSyncAttempt ?? "0") >= 1)
  );
}

async function disableAccidentSending(id: string): Promise<void> {
  await declarationOfflineService.setAccidentSendingById(id, false);
}

async function disableSecuriteSending(id: string): Promise<void> {
  await declarationOfflineService.setSecuriteSendingById(id, false);
}

async function disableDangerSending(id: string): Promise<void> {
  await declarationOfflineService.setDangerSendingById(id, false);
}

const declarationSyncService = {
  synchronizeOfflineAndOnline,
};
export default declarationSyncService;
